// Shared language switcher — replaces /assets/invosly-lang.js for Vite-built
// pages. Persists in localStorage, syncs <html lang>, exposes a hook.

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'invosly_lang';
export type Lang = 'en' | 'th' | 'zh';
const LANGS: ReadonlyArray<readonly [Lang, string]> = [
  ['en', 'EN'],
  ['th', 'ไทย'],
  ['zh', '中文'],
];

let subs: Array<(l: Lang) => void> = [];

function readLang(): Lang {
  try {
    const q = new URLSearchParams(location.search);
    const ql = q.get('lang') as Lang | null;
    if (ql && LANGS.some(x => x[0] === ql)) {
      try { localStorage.setItem(STORAGE_KEY, ql); } catch { /* private mode */ }
      return ql;
    }
  } catch { /* ignore */ }
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
    if (saved && LANGS.some(x => x[0] === saved)) return saved;
  } catch { /* ignore */ }
  const htmlLang = (document.documentElement.lang || '').toLowerCase().slice(0, 2) as Lang;
  if (LANGS.some(x => x[0] === htmlLang)) return htmlLang;
  return 'en';
}

function applyLang(l: Lang): void {
  document.documentElement.lang = l;
  try { localStorage.setItem(STORAGE_KEY, l); } catch { /* private mode */ }
  subs.forEach(fn => { try { fn(l); } catch { /* ignore */ } });
}

export function useInvoslyLang(): [Lang, (l: Lang) => void] {
  const [lang, setLangState] = useState<Lang>(() => readLang());
  useEffect(() => {
    applyLang(lang);
    const fn = (next: Lang) => setLangState(next);
    subs.push(fn);
    return () => { subs = subs.filter(f => f !== fn); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [lang, (next: Lang) => { setLangState(next); applyLang(next); }];
}

type LangSwitcherProps = { light?: boolean };

// Native <select> dropdown, styled by `.lang-select` rules in
// /assets/invosly.css. Use `light` on white backgrounds, omit on navy bgs.
export function LangSwitcher({ light }: LangSwitcherProps) {
  const [lang, setLang] = useInvoslyLang();
  return (
    <div className={'lang-select' + (light ? ' light' : '')}>
      <select value={lang} aria-label="Language"
              onChange={e => setLang(e.target.value as Lang)}>
        {LANGS.map(([code, label]) => (
          <option key={code} value={code}>{label}</option>
        ))}
      </select>
    </div>
  );
}

export function makeT<K extends string>(I18N: Record<Lang, Record<K, any>>) {
  return function useT(): { lang: Lang; t: (k: K) => any } {
    const [lang] = useInvoslyLang();
    return {
      lang,
      t: (k: K) =>
        (I18N[lang] && I18N[lang][k] != null) ? I18N[lang][k] : (I18N.en[k] != null ? I18N.en[k] : k),
    };
  };
}
